import type { SaveData } from '../systems/SaveSystem';

type EffectName = 'footstep' | 'monsterMove' | 'alert' | 'snap' | 'kick' | 'defeat' | 'victory' | 'transition' | 'caught' | 'ui';

export class AudioSystem {
  private context?: AudioContext;
  private musicGain?: GainNode;
  private effectsGain?: GainNode;
  private ambienceGain?: GainNode;
  private musicTimer?: number;
  private ambienceSource?: AudioBufferSourceNode;
  private beat = 0;

  constructor(private settings: SaveData['settings']) {}

  async unlock(): Promise<void> {
    if (!this.context) this.createGraph();
    if (this.context?.state === 'suspended') await this.context.resume();
    this.applySettings(this.settings);
  }

  applySettings(settings: SaveData['settings']): void {
    this.settings = settings;
    const muted = settings.muted ? 0 : 1;
    this.musicGain?.gain.setTargetAtTime(settings.musicVolume * muted * 0.24, this.context?.currentTime ?? 0, 0.04);
    this.ambienceGain?.gain.setTargetAtTime(settings.musicVolume * muted * 0.11, this.context?.currentTime ?? 0, 0.04);
    this.effectsGain?.gain.setTargetAtTime(settings.effectsVolume * muted * 0.42, this.context?.currentTime ?? 0, 0.025);
  }

  startMusic(): void {
    if (!this.context || this.musicTimer) return;
    const melody = [0, 4, 7, 11, 7, 4, 2, 7];
    this.musicTimer = window.setInterval(() => {
      if (!this.context || !this.musicGain || this.context.state !== 'running') return;
      const note = 196 * 2 ** (melody[this.beat % melody.length]! / 12);
      this.tone(note, 0.42, 'sine', this.musicGain, 0.08, -12);
      if (this.beat % 4 === 0) this.tone(note / 2, 1.3, 'triangle', this.musicGain, 0.07, -5);
      this.beat += 1;
    }, 540);
  }

  play(name: EffectName, intensity = 1): void {
    if (!this.context || !this.effectsGain) return;
    switch (name) {
      case 'footstep': this.noise(0.055, 260, 0.07 * intensity); this.tone(92, 0.06, 'triangle', this.effectsGain, 0.04 * intensity, -22); break;
      case 'monsterMove': this.tone(115, 0.11, 'sine', this.effectsGain, 0.035 * intensity, 45); this.tone(82, 0.13, 'triangle', this.effectsGain, 0.028 * intensity, -18, 0.04); break;
      case 'alert': this.tone(520, 0.12, 'square', this.effectsGain, 0.08, 220); this.tone(760, 0.18, 'sine', this.effectsGain, 0.06, -120, 0.08); break;
      case 'snap': this.noise(0.11, 900, 0.16); this.tone(145, 0.16, 'square', this.effectsGain, 0.09, -80); break;
      case 'kick': this.noise(0.085, 420, 0.2); this.tone(115, 0.18, 'triangle', this.effectsGain, 0.16, -65); break;
      case 'defeat': this.tone(260, 0.12, 'sine', this.effectsGain, 0.11, 360); this.tone(520, 0.24, 'triangle', this.effectsGain, 0.09, 320, 0.08); break;
      case 'victory': [0, 4, 7, 12].forEach((step, i) => this.tone(330 * 2 ** (step / 12), 0.42, 'triangle', this.effectsGain!, 0.11, 25, i * 0.12)); break;
      case 'transition': this.tone(210, 0.7, 'sine', this.effectsGain, 0.08, 640); break;
      case 'caught': this.tone(180, 0.35, 'triangle', this.effectsGain, 0.12, -100); this.noise(0.18, 350, 0.08); break;
      case 'ui': this.tone(440, 0.07, 'sine', this.effectsGain, 0.05, 80); break;
    }
  }

  dispose(): void {
    if (this.musicTimer) window.clearInterval(this.musicTimer);
    this.ambienceSource?.stop();
    void this.context?.close();
  }

  private createGraph(): void {
    this.context = new AudioContext();
    this.musicGain = this.context.createGain();
    this.effectsGain = this.context.createGain();
    this.ambienceGain = this.context.createGain();
    const compressor = this.context.createDynamicsCompressor();
    compressor.threshold.value = -12; compressor.knee.value = 12; compressor.ratio.value = 4;
    this.musicGain.connect(compressor); this.effectsGain.connect(compressor); this.ambienceGain.connect(compressor); compressor.connect(this.context.destination);
    const seconds = 4; const buffer = this.context.createBuffer(1, this.context.sampleRate * seconds, this.context.sampleRate);
    const samples = buffer.getChannelData(0); let last = 0;
    for (let i = 0; i < samples.length; i += 1) { last = last * 0.992 + (Math.random() * 2 - 1) * 0.008; samples[i] = last; }
    const source = this.context.createBufferSource(); source.buffer = buffer; source.loop = true;
    const filter = this.context.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 680;
    source.connect(filter); filter.connect(this.ambienceGain); source.start(); this.ambienceSource = source;
  }

  private tone(frequency: number, duration: number, type: OscillatorType, destination: AudioNode, volume: number, sweep = 0, delay = 0): void {
    if (!this.context) return;
    const start = this.context.currentTime + delay;
    const oscillator = this.context.createOscillator(); const gain = this.context.createGain();
    oscillator.type = type; oscillator.frequency.setValueAtTime(frequency, start); oscillator.frequency.linearRampToValueAtTime(Math.max(30, frequency + sweep), start + duration);
    gain.gain.setValueAtTime(0.0001, start); gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, volume), start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain); gain.connect(destination); oscillator.start(start); oscillator.stop(start + duration + 0.03);
  }

  private noise(duration: number, cutoff: number, volume: number): void {
    if (!this.context || !this.effectsGain) return;
    const length = Math.max(1, Math.floor(this.context.sampleRate * duration));
    const buffer = this.context.createBuffer(1, length, this.context.sampleRate); const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / length);
    const source = this.context.createBufferSource(); source.buffer = buffer;
    const filter = this.context.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = cutoff;
    const gain = this.context.createGain(); gain.gain.value = volume;
    source.connect(filter); filter.connect(gain); gain.connect(this.effectsGain); source.start();
  }
}
